import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import { TrendingUp, Zap, Activity } from 'lucide-react';
import { investmentsAPI } from '@/services/api';

// Animated number component with smooth transitions
const AnimatedNumber = ({ value, decimals = 2, prefix = '$' }) => {
  const [displayValue, setDisplayValue] = useState(value);
  const previousValue = useRef(value);
  const animationRef = useRef(null);
  
  useEffect(() => {
    const startValue = previousValue.current;
    const endValue = value;
    const duration = 500; // Animation duration in ms
    const startTime = performance.now();
    
    const animate = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Ease out cubic for smooth deceleration
      const easeProgress = 1 - Math.pow(1 - progress, 3);
      const currentValue = startValue + (endValue - startValue) * easeProgress;
      
      setDisplayValue(currentValue);
      
      if (progress < 1) {
        animationRef.current = requestAnimationFrame(animate);
      } else {
        previousValue.current = endValue;
      }
    };
    
    animationRef.current = requestAnimationFrame(animate);
    
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [value]);
  
  const formattedValue = displayValue.toLocaleString('en-US', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  });
  
  return (
    <span className="font-mono tabular-nums">
      {prefix}{formattedValue}
    </span>
  );
};

export default function LiveProfitTracker() {
  const [profitData, setProfitData] = useState(null);
  const [currentProfit, setCurrentProfit] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const intervalRef = useRef(null);
  const syncIntervalRef = useRef(null);

  // Fetch initial profit data from backend
  const fetchProfitData = useCallback(async () => {
    try {
      const response = await investmentsAPI.getLiveProfit();
      const data = response.data;
      setProfitData(data);
      
      // Calculate current profit based on server time
      if (data.profit_per_second > 0) {
        const serverTime = new Date(data.server_time_utc);
        const dayStart = new Date(data.day_start_utc);
        const elapsedSeconds = (serverTime - dayStart) / 1000;
        const calculatedProfit = (data.profit_per_second * elapsedSeconds) + (data.base_profit || 0);
        setCurrentProfit(calculatedProfit);
        setIsAnimating(true);
      } else {
        setCurrentProfit(0);
        setIsAnimating(false);
      }
      
      setError(null);
    } catch (err) {
      console.error('Failed to fetch profit data:', err);
      setError('Failed to load profit data');
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial fetch
  useEffect(() => {
    fetchProfitData();
    
    // Sync with backend every 30 seconds to ensure accuracy
    syncIntervalRef.current = setInterval(fetchProfitData, 30000);
    
    return () => {
      if (syncIntervalRef.current) {
        clearInterval(syncIntervalRef.current);
      }
    };
  }, [fetchProfitData]);

  // Real-time profit increment
  useEffect(() => {
    if (!profitData || profitData.profit_per_second <= 0) {
      return;
    }

    // Update every 100ms for smooth animation (10 updates per second)
    const updateInterval = 100;
    const profitIncrement = profitData.profit_per_second * (updateInterval / 1000);

    intervalRef.current = setInterval(() => {
      setCurrentProfit(prev => {
        // Check if we've reached midnight (new day)
        const now = new Date();
        const utcMidnight = new Date(now.toISOString().split('T')[0] + 'T00:00:00Z');
        const nextMidnight = new Date(utcMidnight.getTime() + 86400000);
        
        if (now >= nextMidnight) {
          // Reset at midnight - will sync with backend on next interval
          return 0;
        }
        
        return prev + profitIncrement;
      });
    }, updateInterval);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [profitData]);

  // Calculate time until midnight for display
  const getTimeUntilReset = () => {
    const now = new Date();
    const tomorrow = new Date(now);
    tomorrow.setUTCHours(24, 0, 0, 0);
    const diff = tomorrow - now;
    
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
    
    return `${hours}h ${minutes}m`;
  };

  if (loading) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-emerald-900/40 via-emerald-800/30 to-green-900/40 border border-emerald-500/30 p-4 sm:p-6"
      >
        <div className="flex items-center justify-center py-8">
          <div className="w-6 h-6 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
        </div>
      </motion.div>
    );
  }

  if (error || !profitData) {
    return null; // Don't show if there's an error or no data
  }

  // Don't show if user has no active investments
  if (profitData.investments_count === 0) {
    return null;
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-emerald-900/40 via-emerald-800/30 to-green-900/40 border border-emerald-500/30"
      data-testid="live-profit-tracker"
    >
      {/* Animated background glow */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-1/2 -right-1/2 w-full h-full bg-emerald-500/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute -bottom-1/2 -left-1/2 w-full h-full bg-green-500/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
      </div>
      
      <div className="relative p-4 sm:p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="relative">
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                <TrendingUp className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-400" />
              </div>
              {isAnimating && (
                <span className="absolute -top-1 -right-1 w-3 h-3 bg-emerald-500 rounded-full animate-ping" />
              )}
            </div>
            <div>
              <h3 className="text-white font-semibold text-sm sm:text-base">Live Earnings Today</h3>
              <p className="text-emerald-400/70 text-xs flex items-center gap-1">
                <Activity className="w-3 h-3" />
                {isAnimating ? 'Actively earning' : 'Waiting for investments'}
              </p>
            </div>
          </div>
          
          {/* Live indicator */}
          <div className="flex items-center gap-1.5 px-2 py-1 bg-emerald-500/20 rounded-full">
            <span className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
            <span className="text-emerald-400 text-xs font-medium">LIVE</span>
          </div>
        </div>

        {/* Main Profit Display */}
        <div className="text-center py-4 sm:py-6">
          <motion.div
            className="relative inline-block"
            animate={{ 
              textShadow: isAnimating 
                ? ['0 0 20px rgba(16, 185, 129, 0.3)', '0 0 40px rgba(16, 185, 129, 0.5)', '0 0 20px rgba(16, 185, 129, 0.3)']
                : '0 0 0px rgba(16, 185, 129, 0)'
            }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
          >
            <span className="text-3xl sm:text-4xl md:text-5xl font-bold text-emerald-400">
              <AnimatedNumber value={currentProfit} decimals={4} prefix="+$" />
            </span>
          </motion.div>
          <p className="text-white/60 text-sm mt-2">Today's Trading Profit</p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mt-4 pt-4 border-t border-emerald-500/20">
          <div className="text-center">
            <p className="text-white/50 text-xs mb-1">Active Trading</p>
            <p className="text-white font-semibold text-sm sm:text-base">
              ${profitData.total_active_investment.toLocaleString('en-US', { minimumFractionDigits: 2 })}
            </p>
          </div>
          <div className="text-center">
            <p className="text-white/50 text-xs mb-1">Daily Rate</p>
            <p className="text-emerald-400 font-semibold text-sm sm:text-base">
              {profitData.average_daily_rate}%
            </p>
          </div>
          <div className="text-center">
            <p className="text-white/50 text-xs mb-1">Per Second</p>
            <p className="text-cyan-400 font-mono text-xs sm:text-sm">
              +${profitData.profit_per_second.toFixed(6)}
            </p>
          </div>
          <div className="text-center">
            <p className="text-white/50 text-xs mb-1">Resets In</p>
            <p className="text-white/80 font-semibold text-sm sm:text-base">
              {getTimeUntilReset()}
            </p>
          </div>
        </div>

        {/* Active Trading Positions Count */}
        <div className="mt-4 flex items-center justify-center gap-2 text-sm text-white/60">
          <Zap className="w-4 h-4 text-yellow-400" />
          <span>{profitData.investments_count} active trading position{profitData.investments_count !== 1 ? 's' : ''} generating profit</span>
        </div>
      </div>
    </motion.div>
  );
}
